import { useEffect, useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { mellomromFoer, type Del } from './setning.ts';
import type { LeddNoekkel } from './skjema.ts';
import type { Lagretilstand } from './useUtkastLagring.ts';

/**
 * Byggeklossene i innsenderens flate (H2.2), i formen fra `hypotese-innsending.jsx`. Alle mål
 * og farger er tokens i `hypoteser.css`; her står bare struktur og tekst.
 */

/**
 * Sidens ramme: toppen over 900, stripen under (DD-51), innholdet som ruller, og foten som
 * står. Toppen bærer tilbake, sidens tittel og lagringsstatusen — aldri en setning.
 */
export function Flate({
  tittel,
  tilbake,
  status,
  fot,
  children,
  bredde = 'les',
  midt = false,
  onTilbake,
}: {
  tittel: string;
  tilbake: { navn: string; til: string };
  status?: ReactNode;
  fot?: ReactNode;
  children: ReactNode;
  bredde?: 'les' | 'skjema' | 'smal';
  midt?: boolean;
  /** Kalles før tilbakelenken følges; `preventDefault()` holder siden (endringsskjemaet spør først). */
  onTilbake?: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const smal = useSmal();
  return (
    <div className="h-flate">
      {smal ? (
        <header className="h-stripe" data-testid="h-stripe">
          <Link to={tilbake.til} className="panel-stripe-tilbake" aria-label={`Tilbake til ${tilbake.navn}`} onClick={onTilbake}>
            <Ikon navn="chevron-back-outline" />
          </Link>
          <h1 className="h-stripe-tittel">{tittel}</h1>
          {status ? <span className="h-stripe-status">{status}</span> : null}
        </header>
      ) : (
        <header className="h-topp">
          <span className="h-topp-venstre">
            <Link to={tilbake.til} className="panel-knapp-lenke" onClick={onTilbake}>
              <Ikon navn="chevron-back-outline" />
              {tilbake.navn}
            </Link>
          </span>
          <h1 className="h-topp-tittel">{tittel}</h1>
          <span className="h-topp-hoyre">{status}</span>
        </header>
      )}
      <div className={`h-rull${midt && !smal ? ' h-rull-midt' : ''}`}>
        <div className={`h-innhold h-innhold-${bredde}`}>{children}</div>
      </div>
      {fot}
    </div>
  );
}

/**
 * Foten (DD-58): ÉTT primærvalg til høyre med tasten ved siden av; en tekstlenke til venstre;
 * en sperret knapps grunn som setning ved knappen, med `aria-describedby` — aldri en stum
 * deaktivert knapp (`ux-flow-architect`, «Forklarte låser»). Under 900 står grunnen over
 * knappen, og knappen fyller bredden i tommelsonen.
 *
 * `tast` er fotens tast: Ctrl+↵ for skjemaene, ↵ for navnet og kvitteringen (DD-11 presisert
 * 2026-09-26). Ctrl-tastene virker også med fokus i et felt; ↵ alene gjør det ikke.
 */
export function Fot({
  venstre,
  grunn,
  primar,
  primarFoerst = false,
  hoyre,
}: {
  venstre?: ReactNode;
  grunn?: string | null;
  /**
   * Primærknappen først, setningen ved siden av og `hoyre` (en tekstlenke, «Avbryt») helt til
   * høyre — som Innstillinger-foten og konseptets skjerm 6b. Grunnen står da ved knappen.
   */
  primarFoerst?: boolean;
  hoyre?: ReactNode;
  primar: { label: string; onClick: () => void; tast?: 'ctrl-enter' | 'enter' | 'ingen'; opptatt?: boolean; ikon?: string; testid?: string };
}) {
  const smal = useSmal();
  const sperret = Boolean(grunn) || Boolean(primar.opptatt);
  const tast = primar.tast ?? 'ctrl-enter';
  const handling = useRef(primar.onClick);
  handling.current = primar.onClick;
  const sperretRef = useRef(sperret);
  sperretRef.current = sperret;

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (tast === 'ingen' || e.key !== 'Enter' || e.defaultPrevented) return;
      if (tast === 'ctrl-enter' && !(e.ctrlKey || e.metaKey)) return;
      if (tast === 'enter') {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        const mål = e.target as HTMLElement | null;
        if (mål && /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(mål.tagName)) return;
      }
      e.preventDefault();
      if (!sperretRef.current) handling.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tast]);

  const kbd = tast === 'ingen' ? null : tast === 'enter' ? '↵' : 'Ctrl ↵';
  const knapp = (
    <button
      type="button"
      className="panel-knapp h-primar"
      aria-disabled={sperret || undefined}
      aria-busy={primar.opptatt || undefined}
      aria-describedby={grunn ? 'h-fot-grunn' : undefined}
      aria-keyshortcuts={tast === 'ingen' ? undefined : tast === 'enter' ? 'Enter' : 'Control+Enter'}
      onClick={sperret ? undefined : primar.onClick}
      data-testid={primar.testid ?? 'h-primar'}
    >
      {grunn ? <Ikon navn="lock-closed-outline" /> : primar.ikon ? <Ikon navn={primar.ikon} /> : null}
      {primar.label}
      {primar.opptatt || smal || !kbd ? null : <kbd>{kbd}</kbd>}
    </button>
  );
  const grunnen = grunn ? (
    <span id="h-fot-grunn" className="h-grunn" data-testid="h-grunn">
      <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
      <span>{grunn}</span>
    </span>
  ) : null;

  if (smal) {
    return (
      <div className="h-fot h-fot-smal">
        {grunnen}
        <div className="h-fot-rad">
          {venstre}
          {hoyre}
          {knapp}
        </div>
      </div>
    );
  }
  if (primarFoerst) {
    return (
      <div className="h-fot h-fot-primar-foerst">
        {knapp}
        {grunnen}
        {venstre}
        {hoyre ? <span className="h-fot-hoyre">{hoyre}</span> : null}
      </div>
    );
  }
  return (
    <div className="h-fot">
      {grunnen ?? venstre}
      <span className="h-fot-hoyre">
        {grunnen ? venstre : null}
        {/* Et sekundærvalg ved knappen, som kvitteringens «Se hypotesen» (W6). */}
        {hoyre}
        {knapp}
      </span>
    </div>
  );
}

/** Tekstlenke som er en handling (actionText, DD-53): «Lukk», «Fortsett senere», «Fyll ut». */
export function Tekstknapp({ children, onClick, testid }: { children: ReactNode; onClick: () => void; testid?: string }) {
  return (
    <button type="button" className="panel-knapp-lenke" onClick={onClick} data-testid={testid}>
      {children}
    </button>
  );
}

/**
 * Lagringsstatusen (toppen over 900, stripen under): tjenerens svar, aldri nettleserens lager
 * (K8). `role="status"`, så en skjermleser hører «Lagret» uten å flytte fokus. Under 900 står
 * bare «Ikke lagret» — grunnen står i skjemaet, så tittelen i stripen aldri kuttes.
 */
export function Lagret({ tilstand, kl, kort }: { tilstand: Lagretilstand; kl: string | null; kort?: boolean }) {
  const [ikon, tekst]: [string | null, string] =
    tilstand === 'lagret'
      ? ['checkmark-outline', kl ? `Lagret ${kl}` : 'Lagret']
      : tilstand === 'lagrer'
        ? [null, 'Lagrer …']
        : tilstand === 'konflikt'
          ? ['cloud-offline-outline', 'Ikke lagret — velg versjon under']
          : tilstand === 'nett'
            ? ['cloud-offline-outline', 'Ikke lagret — venter på nett']
            : tilstand === 'forLang'
              ? ['cloud-offline-outline', 'Ikke lagret — teksten er for lang']
              : tilstand === 'feil'
                ? ['cloud-offline-outline', 'Ikke lagret']
                : [null, ''];
  const vis = kort && tekst.startsWith('Ikke lagret') ? 'Ikke lagret' : tekst;
  return (
    <span role="status" aria-label={tekst || undefined} className={`h-lagret h-lagret-${tilstand}`} data-testid="h-lagret">
      {ikon ? <Ikon navn={ikon} className="h-lagret-ikon" /> : null}
      {vis}
    </span>
  );
}

/**
 * Setningen som brikker (P1). Hver brikke som hører til et ledd er en knapp som åpner leddet.
 * Tom = stiplet; skrives nå = ramme; mangler = ⚠ + ordet (DD-08). Tekst vises alltid helt.
 */
export function Brikker({ deler, str = 'body', onAapne }: { deler: Del[]; str?: 'body' | 'h3' | 'h1'; onAapne?: (ledd: LeddNoekkel) => void }) {
  return (
    <p className={`h-brikker h-brikker-${str}`} data-testid="h-setning">
      {deler.map((d, i) => {
        const mellom = mellomromFoer(d, i) ? ' ' : '';
        if (d.type === 'fw') return <span key={i}>{mellom + d.tekst}</span>;
        const innhold = (
          <>
            {d.type === 'mangler' ? <Ikon navn="warning-outline" className="h-brikke-ikon" /> : null}
            {d.endret ? <Ikon navn="create-outline" className="h-brikke-ikon h-brikke-endret-ikon" /> : null}
            {d.tekst}
          </>
        );
        return (
          <span key={i}>
            {mellom}
            {onAapne && d.ledd ? (
              <button type="button" className={`h-brikke h-brikke-${d.type}`} onClick={() => onAapne(d.ledd!)} aria-label={d.type === 'mangler' ? `Mangler: ${d.tekst} — åpne leddet` : `${d.endret ? 'Endret: ' : ''}${d.tekst} — endre leddet`}>
                {innhold}
              </button>
            ) : (
              <span className={`h-brikke h-brikke-${d.type}`}>
                {d.endret ? <span className="panel-skjult">Endret: </span> : null}
                {innhold}
              </span>
            )}
          </span>
        );
      })}
    </p>
  );
}

/**
 * Radiogruppe (DD-56, DD-08): segmenter, det valgte med hakemerke; ett tabstopp, piltastene
 * flytter valget. Retningen, baseline, kilden og oppdateringen.
 */
export function Radiogruppe<T extends string>({ etikettId, valg, aktiv, onVelg, testid }: { etikettId: string; valg: Array<[T, string]>; aktiv: T | undefined; onVelg: (v: T) => void; testid?: string }) {
  const rot = useRef<HTMLDivElement>(null);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = valg.findIndex(([k]) => k === aktiv);
    const fram = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const bak = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
    if (!fram && !bak) return;
    e.preventDefault();
    const til = fram ? (i + 1) % valg.length : (i - 1 + valg.length) % valg.length;
    onVelg(valg[til]![0]);
    rot.current?.querySelectorAll<HTMLElement>('[role="radio"]')[til]?.focus();
  };
  return (
    <div ref={rot} role="radiogroup" aria-labelledby={etikettId} className="h-radio" onKeyDown={onKey} data-testid={testid}>
      {valg.map(([k, navn], i) => {
        const paa = aktiv === k;
        return (
          <button key={k} type="button" role="radio" aria-checked={paa} tabIndex={paa || (!aktiv && i === 0) ? 0 : -1} className={paa ? 'h-radio-valg h-radio-paa' : 'h-radio-valg'} onClick={() => onVelg(k)}>
            {paa ? <Ikon navn="checkmark-outline" className="h-radio-hake" /> : null}
            {navn}
          </button>
        );
      })}
    </div>
  );
}

/** Varsel: ikon OG tekst (DD-08); info eller warning, aldri danger — ingenting ble fjernet (DD-53). */
export function Varsel({ children, tone = 'warning', rolle = 'alert', handling }: { children: ReactNode; tone?: 'warning' | 'info'; rolle?: 'alert' | 'status'; handling?: ReactNode }) {
  return (
    <div role={rolle} className={`h-varsel h-varsel-${tone}`}>
      <Ikon navn={tone === 'info' ? 'information-circle-outline' : 'warning-outline'} className="h-varsel-ikon" />
      <span className="h-varsel-tekst">{children}</span>
      {handling}
    </div>
  );
}
